import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Header from './Header.js';
import Picture from './components/Picture.jsx';
import Footer from './Footer.js';
import NewsletterSubscription from './NewsletterSubscription.js';
import { productData } from './productData.js';

const EXTRA_SCROLL_PADDING = 24;
const THEME_ANIMATION_DURATION_MS = 200;
const THEME_ANIMATION_EASING = 'cubic-bezier(0, 0.11, 0.355, 1)';
const THEME_PARALLAX_OFFSET = 100; // align with Salient fade-in baseline (slightly more pronounced)
const THEME_SECONDARY_DELAY_MS = 200;
const APPLICATION_STEP_STAGGER_MS = 170;
const APPLICATION_STEP_STAGGER_MAX_MS = 4960;

const VERTICAL_PRODUCT_IDS = new Set([
  'l1-leather-guard-ab',
  'i1-smart-fabric-ab',
  'c2-liquid-crystal',
  'g1-smart-glass',
  'g5-water-repellent',
]);

const HERO_IMAGE_BASE_CONFIG = {
  default: {
    widthClass: 'w-full',
    marginClasses: '-mt-36 max-sm:-mt-12',
    maxHeight: 'min(80vh, 820px)',
    translateY: '0%',
  },
  vertical: {
    widthClass: 'w-auto',
    marginClasses: 'lg:-mt-26 md:mt-0 max-sm:mt-10',
    maxHeight: 'min(72vh, 700px)',
    translateY: '-6%',
  },
};

const HERO_IMAGE_OVERRIDES = {
  'g1-smart-glass': {
    translateY: '-3%',
    maxHeight: 'min(74vh, 720px)',
  },
};

const SECONDARY_IMAGE_BASE_CONFIG = {
  default: {
    widthClass: 'w-full max-w-md',
    maxHeight: 'min(64vh, 560px)',
    translateY: '0%',
  },
  vertical: {
    widthClass: 'w-auto max-w-[360px]',
    maxHeight: 'min(58vh, 500px)',
    translateY: '-4%',
  },
};

const SECONDARY_IMAGE_OVERRIDES = {
  'g1-smart-glass': {
    translateY: '-2%',
  },
};

const getHeroImageConfig = (productId) => {
  const isVertical = VERTICAL_PRODUCT_IDS.has(productId);
  const base = isVertical ? HERO_IMAGE_BASE_CONFIG.vertical : HERO_IMAGE_BASE_CONFIG.default;
  const overrides = HERO_IMAGE_OVERRIDES[productId] || {};
  return { ...base, ...overrides };
};

const getSecondaryImageConfig = (productId) => {
  const isVertical = VERTICAL_PRODUCT_IDS.has(productId);
  const base = isVertical ? SECONDARY_IMAGE_BASE_CONFIG.vertical : SECONDARY_IMAGE_BASE_CONFIG.default;
  const overrides = SECONDARY_IMAGE_OVERRIDES[productId] || {};
  return { ...base, ...overrides };
};

const normalizeCategoryList = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

const ProductCategoryLabels = ({ categories }) => {
  const normalized = useMemo(() => normalizeCategoryList(categories), [categories]);
  const primary = useMemo(() => normalized.slice(0, 2), [normalized]);
  const remainder = useMemo(() => normalized.slice(2), [normalized]);
  const [wrapped, setWrapped] = useState(false);
  const containerRef = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const checkWrap = () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      const children = Array.from(container.children);
      if (children.length <= 1) {
        setWrapped(false);
        return;
      }

      const firstTop = children[0].offsetTop;
      const anyWrapped = children.some((child) => child.offsetTop - firstTop > 1);
      setWrapped(anyWrapped);
    };

    checkWrap();
    let observer;

    if (typeof IntersectionObserver === 'undefined') {
      setRevealed(true);
    } else {
      observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setRevealed(true);
              obs.disconnect();
            }
          });
        },
        { root: null, threshold: 0.2 }
      );
      if (containerRef.current) observer.observe(containerRef.current);
    }

    let resizeObserver;

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(checkWrap);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }
    } else {
      window.addEventListener('resize', checkWrap);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', checkWrap);
      }
      if (observer) {
        observer.disconnect();
      }
    };
  }, [primary]);

  const isDual = primary.length === 2;
  const shouldOverlap = isDual && !wrapped;

  if (!primary.length) {
    return null;
  }

  const labelGroup = (
    <div
      ref={containerRef}
      className={`flex gap-x-0 gap-y-1 ${shouldOverlap ? 'flex-nowrap' : 'flex-wrap'}`}
    >
      {primary.map((label, index) => {
        const isSecondary = index === 1;
        const paddingClasses = shouldOverlap && isSecondary ? 'pl-5 pr-5' : 'pl-2 pr-4';
        const baseClasses = `inline-flex items-center ${paddingClasses} py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] shadow-md`;

        const classNames = `${baseClasses} text-white`;

        const style = {
          background: '#FF0000',
          clipPath: 'polygon(0% 0%, 100% 0%, calc(100% - 16px) 100%, 0% 100%)',
          border: '1px solid #FF0000',
        };

        if (shouldOverlap) {
          style.clipPath = isSecondary
            ? 'polygon(16px 0%, 100% 0%, calc(100% - 16px) 100%, 0% 100%)'
            : 'polygon(0% 0%, 100% 0%, calc(100% - 16px) 100%, 0% 100%)';

          if (isSecondary) {
            style.marginLeft = '-10px';
          }
        } else if (isDual && wrapped) {
          style.clipPath = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)';
        }

        const overlapClasses = shouldOverlap && isDual ? (isSecondary ? 'relative z-0' : 'relative z-10') : '';

        const textMaskStyle = {
          display: 'inline-block',
          whiteSpace: 'nowrap',
          clipPath: revealed ? 'inset(0% 0% 0% 0%)' : 'inset(0% 100% 0% 0%)',
          transition: `clip-path ${THEME_ANIMATION_DURATION_MS}ms ${THEME_ANIMATION_EASING}`,
        };

        return (
          <span key={`${label}-${index}`} className={`${classNames} ${overlapClasses}`} style={style}>
            <span style={textMaskStyle}>{label}</span>
          </span>
        );
      })}
    </div>
  );

  if (!remainder.length) {
    return labelGroup;
  }

  return (
    <div className="flex flex-wrap items-start gap-y-1">
      {labelGroup}
      {remainder.map((label, index) => {
        const isMirroredPrimary = index === 0;
        const paddingClasses = isMirroredPrimary ? 'pl-5 pr-2' : 'px-4';
        const textColor = isMirroredPrimary ? 'text-white' : 'text-black';
        const spacing = index > 0 ? 'ml-2' : '';

        const style = isMirroredPrimary
          ? {
              background: '#FF0000',
              clipPath: 'polygon(16px 0%, 100% 0%, 100% 100%, 0% 100%)',
              border: '1px solid #FF0000',
            }
          : {
              background: '#FFFFFF',
              clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
              border: '1px solid #FF0000',
            };

        if (isMirroredPrimary && shouldOverlap) {
          style.marginLeft = '-10px';
        }

        const overlapClasses = shouldOverlap && isMirroredPrimary ? 'relative z-0' : '';

        const textMaskStyle = {
          display: 'inline-block',
          whiteSpace: 'nowrap',
          clipPath: revealed ? 'inset(0% 0% 0% 0%)' : 'inset(0% 100% 0% 0%)',
          transition: `clip-path ${THEME_ANIMATION_DURATION_MS}ms ${THEME_ANIMATION_EASING}`,
        };

        return (
          <span
            key={`${label}-extra-${index}`}
            className={`inline-flex items-center ${paddingClasses} py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] ${textColor} shadow-md ${spacing} ${overlapClasses}`}
            style={style}
          >
            <span style={textMaskStyle}>{label}</span>
          </span>
        );
      })}
    </div>
  );
};

const SectionLabel = ({ children }) => {
  const labelRef = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const element = labelRef.current;
    if (!element) {
      return undefined;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setRevealed(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setRevealed(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <span
      ref={labelRef}
      className="relative inline-flex items-center overflow-hidden pl-6 pr-6 py-1.5 text-xs font-bold uppercase tracking-[0.1em] text-white shadow-md"
      style={{
        clipPath: 'polygon(0% 0%, 100% 0%, calc(100% - 16px) 100%, 0% 100%)',
        border: '1px solid #FF0000',
      }}
    >
      <span
        className="absolute inset-0"
        style={{
          background: '#FF0000',
          transform: revealed ? 'translate3d(0, 0, 0)' : 'translate3d(-101%, 0, 0)',
          transition: `transform ${THEME_ANIMATION_DURATION_MS}ms ${THEME_ANIMATION_EASING}`,
          willChange: 'transform',
        }}
      />
      <span
        className="relative z-10"
        style={{
          clipPath: revealed ? 'inset(0% 0% 0% 0%)' : 'inset(0% 100% 0% 0%)',
          transition: `clip-path ${THEME_ANIMATION_DURATION_MS}ms ${THEME_ANIMATION_EASING}`,
          willChange: 'clip-path',
        }}
      >
        {children}
      </span>
    </span>
  );
};

const ProductTemplate = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [activeFaq, setActiveFaq] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [navMetrics, setNavMetrics] = useState({ header: 0, height: 0 });
  const productDescriptionRef = useRef(null);
  const applicationRef = useRef(null);
  const faqsRef = useRef(null);
  const navRef = useRef(null);
  const navMetricsRef = useRef(navMetrics);
  const productDescAnimRef = useRef(null);
  const applicationAnimRef = useRef(null);
  const [descRevealed, setDescRevealed] = useState(false);
  const [appRevealed, setAppRevealed] = useState(false);
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });
  const [isZoomOverflowLocked, setIsZoomOverflowLocked] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  const [isLargeViewport, setIsLargeViewport] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  );

  useEffect(() => {
    navMetricsRef.current = navMetrics;
  }, [navMetrics]);

  useEffect(() => {
    const foundProduct = productData.find((item) => item.id === id);
    setProduct(foundProduct || null);
    setActiveFaq(null);
    window.scrollTo({ top: 0 });
  }, [id]);

  const sections = useMemo(() => {
    if (!product) {
      return [];
    }

    const availableSections = [];

    if (product.mainSection?.paragraphs?.length) {
      availableSections.push({ id: 'productDescription', label: 'Detalhes do Produto', ref: productDescriptionRef });
    }

    if (product.application?.steps?.length) {
      availableSections.push({ id: 'application', label: 'Informações de Aplicação', ref: applicationRef });
    }

    if (product.faqs?.items?.length) {
      availableSections.push({ id: 'faqs', label: 'FAQs', ref: faqsRef });
    }

    return availableSections;
  }, [product]);

  useEffect(() => {
    if (!sections.length) {
      return;
    }

    if (!sections.some((section) => section.id === activeSection)) {
      setActiveSection(sections[0].id);
    }
  }, [sections, activeSection]);

  // One-time reveal observers for description and application header
  useEffect(() => {
    setDescRevealed(false);
    setAppRevealed(false);

    if (typeof IntersectionObserver === 'undefined') {
      setDescRevealed(true);
      setAppRevealed(true);
      return () => {};
    }

    const descEl = productDescAnimRef.current;
    const appEl = applicationAnimRef.current;

    const opts = { root: null, threshold: 0.5, rootMargin: '0px 0px -10% 0px' };

    const descObs = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setDescRevealed(true);
          obs.disconnect();
        }
      });
    }, opts);

    const appObs = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setAppRevealed(true);
          obs.disconnect();
        }
      });
    }, opts);

    const revealIfVisible = () => {
      if (descEl) {
        const rect = descEl.getBoundingClientRect();
        if (rect.top <= window.innerHeight && rect.bottom >= 0) {
          setDescRevealed(true);
          descObs.disconnect();
        }
      }
      if (appEl) {
        const rect = appEl.getBoundingClientRect();
        if (rect.top <= window.innerHeight && rect.bottom >= 0) {
          setAppRevealed(true);
          appObs.disconnect();
        }
      }
    };

    if (descEl) descObs.observe(descEl);
    if (appEl) appObs.observe(appEl);

    // Immediate check in case the elements are already in view (e.g. after refresh)
    revealIfVisible();

    const fallbackDescTimeout = window.setTimeout(() => {
      setDescRevealed(true);
      descObs.disconnect();
    }, THEME_ANIMATION_DURATION_MS + 1300);

    const fallbackAppTimeout = window.setTimeout(() => {
      setAppRevealed(true);
      appObs.disconnect();
    }, THEME_ANIMATION_DURATION_MS + 1500);

    return () => {
      descObs.disconnect();
      appObs.disconnect();
      window.clearTimeout(fallbackDescTimeout);
      window.clearTimeout(fallbackAppTimeout);
    };
  }, [id]);

  useEffect(() => {
    const header = document.querySelector('header');

    let animationFrameId = null;
    let transitionFrameId = null;
    let isWatchingTransition = false;

    const updateMetrics = () => {
      animationFrameId = null;

      const headerBottom = header ? Math.max(header.getBoundingClientRect().bottom, 0) : 0;
      const navHeight = navRef.current ? navRef.current.getBoundingClientRect().height : 0;

      setNavMetrics((prev) => {
        const next = { header: headerBottom, height: navHeight };
        if (Math.abs(prev.header - next.header) < 0.5 && Math.abs(prev.height - next.height) < 0.5) {
          return prev;
        }
        return next;
      });
    };

    const requestUpdate = () => {
      if (animationFrameId) {
        return;
      }
      animationFrameId = requestAnimationFrame(updateMetrics);
    };

    const stopTransitionWatcher = () => {
      if (!isWatchingTransition) {
        return;
      }
      isWatchingTransition = false;
      if (transitionFrameId) {
        cancelAnimationFrame(transitionFrameId);
        transitionFrameId = null;
      }
      requestUpdate();
    };

    const startTransitionWatcher = () => {
      if (isWatchingTransition) {
        return;
      }
      isWatchingTransition = true;

      const tick = () => {
        if (!isWatchingTransition) {
          return;
        }
        requestUpdate();
        transitionFrameId = requestAnimationFrame(tick);
      };

      tick();
    };

    requestUpdate();

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);

    const navElement = navRef.current;
    let resizeObserver;

    if (navElement && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(requestUpdate);
      resizeObserver.observe(navElement);
    }

    let mutationObserver;

    if (header && typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(requestUpdate);
      mutationObserver.observe(header, { attributes: true, attributeFilter: ['class', 'style'] });
    }

    if (header) {
      header.addEventListener('transitionrun', startTransitionWatcher);
      header.addEventListener('transitionstart', startTransitionWatcher);
      header.addEventListener('transitionend', stopTransitionWatcher);
      header.addEventListener('transitioncancel', stopTransitionWatcher);
    }

    return () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      stopTransitionWatcher();
      if (resizeObserver && navElement) {
        resizeObserver.unobserve(navElement);
      }
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
      if (header) {
        header.removeEventListener('transitionrun', startTransitionWatcher);
        header.removeEventListener('transitionstart', startTransitionWatcher);
        header.removeEventListener('transitionend', stopTransitionWatcher);
        header.removeEventListener('transitioncancel', stopTransitionWatcher);
      }
    };
  }, [sections.length]);

  const computeActiveSection = useCallback(() => {
    if (!sections.length) {
      return;
    }

    const { header: headerOffset, height: navHeight } = navMetricsRef.current;
    const referencePoint = window.scrollY + headerOffset + navHeight + EXTRA_SCROLL_PADDING + 1;

    let currentSectionId = sections[0]?.id || null;

    for (let index = 0; index < sections.length; index += 1) {
      const element = sections[index].ref?.current;
      if (!element) {
        continue;
      }

      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      if (referencePoint >= elementTop) {
        currentSectionId = sections[index].id;
      } else {
        break;
      }
    }

    if (currentSectionId) {
      setActiveSection((prev) => (prev === currentSectionId ? prev : currentSectionId));
    }
  }, [sections]);

  useEffect(() => {
    if (!sections.length) {
      return undefined;
    }

    let animationFrameId = null;

    const handleScroll = () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      animationFrameId = requestAnimationFrame(() => {
        animationFrameId = null;
        computeActiveSection();
      });
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [computeActiveSection, sections.length]);

  useEffect(() => {
    if (!sections.length) {
      return;
    }

    computeActiveSection();
  }, [computeActiveSection, navMetrics, sections.length]);

  const handleScrollToSection = (sectionId) => {
    const section = sections.find((item) => item.id === sectionId);
    const element = section?.ref?.current;
    if (!element) {
      return;
    }

    const { header: headerOffset, height: navHeight } = navMetricsRef.current;
    const totalOffset = headerOffset + navHeight + EXTRA_SCROLL_PADDING;
    const offsetTop = element.getBoundingClientRect().top + window.scrollY - totalOffset;
    window.scrollTo({ top: offsetTop, behavior: 'smooth' });

  };

  const toggleFaq = (index) => {
    setActiveFaq((prev) => (prev === index ? null : index));
  };

  const getYoutubeEmbedUrl = (url) => {
    if (!url) {
      return null;
    }

    try {
      const parsed = new URL(url);
      if (parsed.hostname.includes('youtu.be')) {
        const id = parsed.pathname.replace('/', '').trim();
        return id ? `https://www.youtube.com/embed/${id}` : null;
      }

      if (parsed.hostname.includes('youtube.com')) {
        const id = parsed.searchParams.get('v');
        return id ? `https://www.youtube.com/embed/${id}` : null;
      }
    } catch (error) {
      return null;
    }

    return null;
  };

  const productId = product?.id;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobileViewport(width < 768);
      setIsLargeViewport(width >= 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const productImageFilename = product?.highResImage || product?.image || null;

  const heroImageConfig = useMemo(() => {
    if (!productId) {
      return HERO_IMAGE_BASE_CONFIG.default;
    }
    return getHeroImageConfig(productId);
  }, [productId]);

  const secondaryImageConfig = useMemo(() => {
    if (!productId) {
      return SECONDARY_IMAGE_BASE_CONFIG.default;
    }
    return getSecondaryImageConfig(productId);
  }, [productId]);

  const isVerticalProductImage = productId ? VERTICAL_PRODUCT_IDS.has(productId) : false;
  const zoomImageSrc = productImageFilename ? `/images/${productImageFilename}` : null;
  const zoomLabels = useMemo(() => {
    const base = product?.title ? `imagem do produto ${product.title}` : 'imagem do produto';
    return {
      expand: `Ampliar ${base}`,
      collapse: `Reduzir ${base}`,
    };
  }, [product?.title]);

  const heroZoomScale = isMobileViewport ? 3.1 : 2.1;
  const secondaryZoomScale = isMobileViewport ? 3.1 : 2.1;

  const heroBaseTranslate = useMemo(
    () => `translate3d(0, ${heroImageConfig.translateY}, 0)`,
    [heroImageConfig.translateY]
  );

  const secondaryBaseTranslate = useMemo(
    () => `translate3d(0, ${secondaryImageConfig.translateY}, 0)`,
    [secondaryImageConfig.translateY]
  );

  const handleToggleZoom = useCallback(() => {
    if (!zoomImageSrc) {
      return;
    }
    setIsImageZoomed((prev) => !prev);
  }, [zoomImageSrc]);

  const updateZoomOrigin = useCallback((event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return;
    }

    const rawX = ((event.clientX - rect.left) / rect.width) * 100;
    const rawY = ((event.clientY - rect.top) / rect.height) * 100;

    setZoomOrigin({
      x: Math.min(100, Math.max(0, rawX)),
      y: Math.min(100, Math.max(0, rawY)),
    });
  }, []);

  const handleZoomPointerDown = useCallback(
    (event) => {
      if (!zoomImageSrc) {
        return;
      }
      updateZoomOrigin(event);
    },
    [updateZoomOrigin, zoomImageSrc]
  );

  const handleZoomPointerMove = useCallback(
    (event) => {
      if (!isImageZoomed) {
        return;
      }

      updateZoomOrigin(event);
    },
    [isImageZoomed, updateZoomOrigin]
  );

  const handleZoomPointerLeave = useCallback(() => {
    if (!isImageZoomed) {
      return;
    }
    setZoomOrigin({ x: 50, y: 50 });
  }, [isImageZoomed]);

  useEffect(() => {
    if (!isImageZoomed || typeof document === 'undefined') {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsImageZoomed(false);
        setZoomOrigin({ x: 50, y: 50 });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isImageZoomed]);

  useEffect(() => {
    if (isImageZoomed) {
      setIsZoomOverflowLocked(true);
      return undefined;
    }

    if (typeof window === 'undefined') {
      setZoomOrigin({ x: 50, y: 50 });
      setIsZoomOverflowLocked(false);
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setZoomOrigin({ x: 50, y: 50 });
      setIsZoomOverflowLocked(false);
    }, 200);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [isImageZoomed]);

  useEffect(() => {
    setIsImageZoomed(false);
    setZoomOrigin({ x: 50, y: 50 });
    setIsZoomOverflowLocked(false);
  }, [productId]);

  if (!product) {
    return (
      <div className="min-h-screen bg-white text-gray-800">
        <Header />
        <main className="container mx-auto px-4 pt-32 pb-16">
          <p className="text-center text-lg">Produto não encontrado.</p>
        </main>
        <NewsletterSubscription />
        <Footer />
      </div>
    );
  }

  const isProfessionalApplicationOnly =
    typeof product.category === 'string' &&
    product.category.trim().toLowerCase() === 'professional application only';

  const detailerLocatorHref =
    (typeof product.detailerLocatorUrl === 'string' && product.detailerLocatorUrl.trim()) ||
    '/encontre-um-profissional';

  const becomeApplicatorHref = '/seja-um-aplicador-oficial';

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Header />
      <main>
        <div className="relative z-10 h-32 w-full overflow-hidden bg-[#0c0c0c] dark:bg-[#232324] md:h-40">
          <picture>
            <source srcSet="/images/hero-banner.webp" type="image/webp" />
            <img
              src="/images/hero-banner.png"
              alt="Gtechniq performance banner"
              className="absolute inset-0 h-full w-full object-cover"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/20" />
        </div>
        <section className="bg-white lg:pt-14 pb-16 md:pt-20">
          <div className="container mx-auto px-4 md:px-10">
            <div className="grid items-start gap-24 lg:grid-cols-[minmax(0,_2fr)_minmax(0,_1fr)]">
              <div className="relative w-full lg:self-start">
                <div
                  className={`relative mx-auto flex w-full items-center justify-center px-4 pb-6 md:px-8 lg:px-0 lg:pb-8 lg:max-w-[1080px] md:max-w-[640px] sm:max-w-[640px] ${
                    isZoomOverflowLocked || isVerticalProductImage ? 'overflow-hidden' : 'overflow-visible'
                  }`}
                >
                  <button
                    type="button"
                    onClick={handleToggleZoom}
                    onPointerDown={handleZoomPointerDown}
                    onPointerMove={handleZoomPointerMove}
                    onPointerLeave={handleZoomPointerLeave}
                    disabled={!zoomImageSrc}
                    className={`group relative flex w-full items-center justify-center focus:outline-none transition-[cursor] ${
                      zoomImageSrc
                        ? isImageZoomed
                          ? 'cursor-zoom-out'
                          : 'cursor-zoom-in'
                        : 'cursor-default'
                    }`}
                    aria-label={isImageZoomed ? zoomLabels.collapse : zoomLabels.expand}
                    aria-pressed={isImageZoomed}
                  >
                    <Picture
                      src={`/images/${product.image}`}
                      alt={product.title}
                      className={`z-1 object-contain transition-transform duration-200 ease-out ${
                        [
                          'lg:max-w-[1080px]',
                          'md:max-w-[720px]',
                          'sm:max-w-[640px]',
                          heroImageConfig.widthClass,
                          heroImageConfig.marginClasses,
                        ]
                          .filter(Boolean)
                          .join(' ')
                      } ${zoomImageSrc && !isImageZoomed ? 'group-hover:scale-[1.01]' : ''}`}
                      style={{
                        transform: isImageZoomed
                          ? `${heroBaseTranslate} scale(${heroZoomScale})`
                          : heroBaseTranslate,
                        transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
                        maxHeight: heroImageConfig.maxHeight,
                        transition: isImageZoomed
                          ? 'transform 140ms ease-out'
                          : `transform ${THEME_ANIMATION_DURATION_MS}ms ${THEME_ANIMATION_EASING}`,
                      }}
                    />
                    {zoomImageSrc && !isImageZoomed && (
                      <span className="pointer-events-none absolute bottom-6 right-6 hidden items-center gap-2 rounded-full bg-black/70 px-3 py-2 text-xs font-semibold uppercase tracking-[0.1em] text-white shadow-lg lg:flex">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          className="h-4 w-4"
                        >
                          <path d="M11 4H4v7" />
                          <path d="M13 20h7v-7" />
                          <path d="M21 3l-7.5 7.5" />
                          <path d="M3 21l7.5-7.5" />
                        </svg>
                        Ampliar
                      </span>
                    )}
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-8 lg:-mt-0 md:-mt-36 max-sm:-mt-28">
                <div className="flex flex-col gap-2">
                  <ProductCategoryLabels categories={product.categories} />
                  {isProfessionalApplicationOnly && (
                    <span className="inline-flex items-center bg-black px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-white shadow-md self-start">
                      Professional Application Only
                    </span>
                  )}
                </div>

                <div className="space-y-4">
                  <h1 className="text-4xl font-black tracking-tight text-gray-900 md:text-5xl">{product.title}</h1>
                  {isProfessionalApplicationOnly && (
                    <p className="text-lg font-semibold uppercase tracking-[0.1em] text-[#ff0000]">
                      Uso Exclusivo de Aplicadores Oficiais
                    </p>
                  )}
                  {product.description && (
                    <p className="max-w-2xl text-lg leading-relaxed text-gray-700">{product.description}</p>
                  )}
                </div>

                {product.quickOverview?.length > 0 && (
                  <div className="bg-white ">
                    <div className="px-8 pt-6">
                      <span className="text-xs font-semibold uppercase tracking-[0.4em] text-[#ed1c24]">Destaques</span>
                    </div>
                    <ul className="space-y-4 px-8 pb-6 pt-4">
                      {product.quickOverview.map((item, index) => (
                        <li key={`${item}-${index}`} className="flex items-start gap-4 text-gray-800">
                          <span className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center text-[#ed1c24]">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="none"
                              className="h-4 w-4"
                            >
                              <path
                                d="M16.25 5.5L8.5 14.5L4.25 10"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          <span className="text-base leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className={isProfessionalApplicationOnly ? 'flex flex-col gap-3' : 'flex flex-wrap gap-4'}>
                  {isProfessionalApplicationOnly ? (
                    <>
                      <a
                        href={detailerLocatorHref}
                        className="inline-flex w-full items-center justify-center bg-[#FF0000] px-8 py-4 text-sm font-bold uppercase tracking-[0.1em] text-white shadow-md transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#000000]"
                      >
                        Encontre um profissional
                      </a>
                      <a
                        href={becomeApplicatorHref}
                        className="inline-flex w-full items-center justify-center bg-[#FF0000] px-8 py-4 text-sm font-bold uppercase tracking-[0.1em] text-white shadow-md transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#000000]"
                      >
                        Se torne um aplicador oficial
                      </a>
                    </>
                  ) : (
                    product.shopUrl && (
                      <a
                        href={product.shopUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex w-full items-center justify-center bg-[#FF0000] px-8 py-4 text-sm font-bold uppercase tracking-[0.1em] text-white shadow-md transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#000000]"
                      >
                        Comprar Agora
                      </a>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {sections.length > 0 && isLargeViewport && (
          <nav
            ref={navRef}
            className="sticky top-0 z-40 border-y border-[#FF0000] bg-[#FF0000] shadow-[0_12px_30px_rgba(15,23,42,0.18)] transition-[top] duration-300"
            style={{ top: navMetrics.header }}
          >
            <div className="container mx-auto px-4 py-2 md:px-10">
              <div className="hidden w-full items-center justify-center lg:flex">
                <div className="flex flex-wrap items-center justify-center gap-x-1">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => handleScrollToSection(section.id)}
                      className={`relative px-5 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-white transition-colors duration-200 xl:px-8 xl:text-xs ${
                        activeSection === section.id ? 'bg-white/15 shadow-inner' : 'hover:bg-white/10'
                      }`}
                      aria-current={activeSection === section.id ? 'page' : undefined}
                    >
                      {section.label}
                      {activeSection === section.id && (
                        <span className="absolute inset-x-4 bottom-1.5 block h-0.5 rounded-full bg-white/80 xl:inset-x-5" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </nav>
        )}

        <div className="container mx-auto space-y-20 px-4 md:px-10 py-20">
          {product.mainSection?.paragraphs?.length > 0 && (
            <section
              ref={productDescriptionRef}
              className="grid gap-12 lg:grid-cols-[1.05fr,0.95fr] lg:items-start"
            >
              <div
                ref={productDescAnimRef}
                className="space-y-6"
                style={{
                  opacity: descRevealed ? 1 : 0,
                  transform: descRevealed
                    ? 'translate3d(0, 0, 0)'
                    : `translate3d(0, ${THEME_PARALLAX_OFFSET}px, 0)`,
                  transition: `opacity ${THEME_ANIMATION_DURATION_MS}ms ${THEME_ANIMATION_EASING}, transform ${THEME_ANIMATION_DURATION_MS}ms ${THEME_ANIMATION_EASING}`,
                  willChange: 'transform, opacity',
                }}
              >
                <div className="flex items-center gap-3">
                  <SectionLabel>Descrição do produto</SectionLabel>
                  
                </div>
                <h2 className="text-3xl font-bold text-gray-900 md:text-[25.5px]">
                  {product.mainSection.heading || 'Product Description'}
                </h2>
                <div className="space-y-5 text-[15px] leading-relaxed text-gray-700">
                  {product.mainSection.paragraphs.map((paragraph, index) => (
                    <p key={`main-section-${index}`}>{paragraph}</p>
                  ))}
                </div>
              </div>

              <div
                className="space-y-6"
                style={{
                  opacity: descRevealed ? 1 : 0,
                  transform: descRevealed
                    ? 'translate3d(0, 0, 0)'
                    : `translate3d(0, ${THEME_PARALLAX_OFFSET}px, 0)`,
                  transition: `opacity ${THEME_ANIMATION_DURATION_MS}ms ${THEME_ANIMATION_EASING} ${descRevealed ? THEME_SECONDARY_DELAY_MS : 0}ms, transform ${THEME_ANIMATION_DURATION_MS}ms ${THEME_ANIMATION_EASING} ${descRevealed ? THEME_SECONDARY_DELAY_MS : 0}ms`,
                  willChange: 'transform, opacity',
                }}
              >
                {(() => {
                  const embedUrl = getYoutubeEmbedUrl(product.videoUrl);
                  if (embedUrl) {
                    return (
                      <div className="overflow-hidden shadow-[0_18px_50px_rgba(15,23,42,0.22)]">
                        <div className="relative  w-full pt-[56.25%] bg-black">
                          <iframe
                            src={`${embedUrl}?rel=0`}
                            title={`Como aplicar ${product.title}`}
                            className="absolute inset-0 h-full w-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div className="relative overflow-hidden bg-gradient-to-br from-[#0c0d14] via-[#111520] to-[#2d0c12] p-10 shadow-[0_18px_50px_rgba(15,23,42,0.22)]">
                      <div className="absolute inset-0 opacity-40">
                        <picture>
                          <source srcSet="/images/background/paint-protection-image-min-1-scaled.webp" type="image/webp" />
                          <img
                            src="/images/background/paint-protection-image-min-1-scaled.png"
                            alt="Gtechniq abstract background"
                            className="h-full w-full object-cover"
                          />
                        </picture>
                      </div>
                      <div
                        className={`relative z-10 flex h-full items-center justify-center ${
                          isZoomOverflowLocked ? 'overflow-hidden' : ''
                        }`}
                      >
                        <button
                          type="button"
                          onClick={handleToggleZoom}
                          onPointerDown={handleZoomPointerDown}
                          onPointerMove={handleZoomPointerMove}
                          onPointerLeave={handleZoomPointerLeave}
                          disabled={!zoomImageSrc}
                          className={`relative flex items-center justify-center focus:outline-none transition-[cursor] ${
                            zoomImageSrc
                              ? isImageZoomed
                                ? 'cursor-zoom-out'
                                : 'cursor-zoom-in'
                              : 'cursor-default'
                          }`}
                          aria-label={isImageZoomed ? zoomLabels.collapse : zoomLabels.expand}
                          aria-pressed={isImageZoomed}
                        >
                          <Picture
                            src={`/images/${product.image}`}
                            alt={product.title}
                            className={`drop-shadow-[0_30px_60px_rgba(0,0,0,0.45)] transition-transform duration-200 ease-out ${
                              secondaryImageConfig.widthClass
                            } ${zoomImageSrc && !isImageZoomed ? 'hover:scale-[1.01]' : ''}`}
                            style={{
                              transform: isImageZoomed
                                ? `${secondaryBaseTranslate} scale(${secondaryZoomScale})`
                                : secondaryBaseTranslate,
                              transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
                              maxHeight: secondaryImageConfig.maxHeight,
                              transition: isImageZoomed
                                ? 'transform 140ms ease-out'
                                : `transform ${THEME_ANIMATION_DURATION_MS}ms ${THEME_ANIMATION_EASING}`,
                            }}
                          />
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </section>
          )}

          {product.application?.steps?.length > 0 && (
            <section ref={applicationRef} className="space-y-10">
              <div
                ref={applicationAnimRef}
                className="space-y-4"
                style={{
                  opacity: appRevealed ? 1 : 0,
                  transform: appRevealed
                    ? 'translate3d(0, 0, 0)'
                    : `translate3d(0, ${THEME_PARALLAX_OFFSET}px, 0)`,
                  transition: `opacity ${THEME_ANIMATION_DURATION_MS}ms ${THEME_ANIMATION_EASING}, transform ${THEME_ANIMATION_DURATION_MS}ms ${THEME_ANIMATION_EASING}`,
                  willChange: 'transform, opacity',
                }}
              >
                <div className="flex items-center gap-3">
                  <SectionLabel>Como Aplicar</SectionLabel>
                  
                </div>
                <h2 className="text-3xl font-bold text-gray-900 md:text-[25.5px]">
                  Etapas para obter desempenho máximo do produto.
                </h2>
              </div>
              <ul className="text-[15px] leading-relaxed text-gray-700 md:columns-2 md:gap-x-8">
                {product.application.steps.map((step, index) => {
                  const delay = appRevealed
                    ? `${Math.min(index * APPLICATION_STEP_STAGGER_MS, APPLICATION_STEP_STAGGER_MAX_MS)}ms`
                    : '0ms';
                  return (
                    <li
                      key={`application-step-${index}`}
                      className="mb-4 flex items-start gap-3 break-inside-avoid last:mb-0"
                      style={{
                        opacity: appRevealed ? 1 : 0,
                        transform: appRevealed
                          ? 'translate3d(0, 0, 0)'
                          : 'translate3d(-18px, 24px, 0)',
                        transition: `opacity ${THEME_ANIMATION_DURATION_MS}ms ${THEME_ANIMATION_EASING}, transform ${THEME_ANIMATION_DURATION_MS}ms ${THEME_ANIMATION_EASING}`,
                        transitionDelay: appRevealed ? delay : '0ms',
                        willChange: 'transform, opacity',
                      }}
                    >
                      <span className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center text-[#ff0000]">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="none"
                          className="h-4 w-4"
                        >
                          <path
                            d="M16.25 5.5L8.5 14.5L4.25 10"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      <span>{step}</span>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {product.faqs?.items?.length > 0 && (
            <section
              ref={faqsRef}
              className={[
                'grid gap-10 border border-[#e5e7ef] bg-[#f6f7fb] p-10 shadow-[0_26px_60px_rgba(15,23,42,0.12)]',
                'lg:grid-cols-[0.85fr,1.15fr]',
              ].join(' ')}
            >
              <div className="relative overflow-hidden bg-[#0b0f1a] dark:bg-[#232324] text-white">
                <picture>
                  <source srcSet="/images/background/blue-car-square.webp" type="image/webp" />
                  <img
                    src="/images/background/blue-car-square.jpg"
                    alt="Gtechniq texture"
                    className="absolute inset-0 h-full w-full object-cover opacity-40"
                  />
                </picture>
                <div className="absolute inset-0 bg-gradient-to-br from-[#ed1c24]/70 via-[#111725]/85 to-[#111725]" />
                <div className="relative z-10 flex h-full flex-col justify-between p-10">
                  <div className="space-y-6">
                    <span className="inline-flex items-center border border-white/40 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-white/80">
                      {product.faqs.subheading || 'Need Help?'}
                    </span>
                    <h2 className="text-3xl font-bold leading-snug md:text-4xl">
                      {product.faqs.heading || 'Frequently Asked Questions'}
                    </h2>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                {product.faqs.items.map((faq, index) => {
                  const isOpen = activeFaq === index;
                  return (
                    <div key={`${faq.question}-${index}`} className="overflow-hidden bg-white shadow-[0_14px_30px_rgba(15,23,42,0.08)]">
                      <button
                        type="button"
                        className="flex w-full items-center justify-between px-6 py-5 text-left text-base font-semibold text-gray-900 transition-colors duration-200 hover:bg-gray-50"
                        onClick={() => toggleFaq(index)}
                        aria-expanded={isOpen}
                      >
                        <span>{faq.question}</span>
                        <span className="ml-4 flex h-9 w-9 items-center justify-center bg-[#ed1c24]/10 text-xl font-bold text-[#ed1c24]">
                          {isOpen ? '−' : '+'}
                        </span>
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-6 text-base leading-relaxed text-gray-700">
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="space-y-3 rounded-md bg-white px-6 py-8 text-center shadow-[0_14px_30px_rgba(15,23,42,0.08)]">
                  <p className="text-sm font-semibold uppercase tracking-[0.3em] text-gray-900">Não encontrou sua pergunta?</p>
                  <a
                    href="https://gtechniq.com.br/contato"
                    className="inline-flex w-full items-center justify-center bg-[#ed1c24] px-6 py-3 text-sm font-semibold uppercase tracking-[0.2em] text-white transition-colors duration-200 hover:bg-[#ff3b42]"
                  >
                    Fale Conosco
                  </a>
                </div>
              </div>
            </section>
          )}
        </div>
      </main>
      <NewsletterSubscription />
      <Footer />
    </div>
  );
};

export default ProductTemplate;
